import { IMPORT_SLICE } from '@itc/shared';
import { z } from 'zod';
import { CATEGORIES } from './rules.ts';

// The Blueprint Library's shapes (plan 4A), used by the server and the browser.

/** The wiki's id for a blueprint. */
export const BlueprintUuid = z.string().regex(/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/, 'must be a wiki uuid');

export const CategoryName = z.enum(CATEGORIES);

export const Blueprint = z.object({
  uuid: z.string(),
  name: z.string(),
  /** `Weapon · Medium`. */
  type: z.string(),
  category: CategoryName,
  /** The ingredients' names, comma-separated. */
  materials: z.string(),
  isDefault: z.boolean(),
  owned: z.boolean(),
  /** False once the wiki stops listing it. */
  inWiki: z.boolean(),
});
export type Blueprint = z.infer<typeof Blueprint>;

/** How one sync with the wiki went. */
export const SyncResult = z.object({
  status: z.enum(['ok', 'failed']),
  at: z.iso.datetime(),
  /** Rows the wiki listed. */
  listed: z.number().int(),
  added: z.number().int(),
  changed: z.number().int(),
  gone: z.number().int(),
  /** Why it stopped. Empty when it didn't. */
  error: z.string(),
});
export type SyncResult = z.infer<typeof SyncResult>;

export const Stats = z.object({
  total: z.number().int(),
  owned: z.number().int(),
  missing: z.number().int(),
  percent: z.number().int(),
  ownedGone: z.number().int(),
  categories: z.record(CategoryName, z.number().int()),
  /** The catalogue's game version, as the wiki gives it. */
  gameVersion: z.string(),
  /** The newest sync that worked, or null if none has. */
  syncedAt: z.iso.datetime().nullable(),
  /** The newest sync, if it failed, so the page can say why. */
  failure: SyncResult.nullable(),
});
export type Stats = z.infer<typeof Stats>;

export const ListQuery = z.object({
  q: z.string().trim().max(100).default(''),
  category: CategoryName.optional(),
  show: z.enum(['all', 'owned', 'missing']).default('all'),
  /** Opaque to the browser: how many rows it has. */
  cursor: z.string().regex(/^[0-9]{1,6}$/).optional(),
});
export type ListQuery = z.input<typeof ListQuery>;

export const BlueprintPage = z.object({
  items: z.array(Blueprint),
  nextCursor: z.string().nullable(),
  /** How many rows the filters match, over every page. */
  matched: z.number().int(),
  stats: Stats,
});
export type BlueprintPage = z.infer<typeof BlueprintPage>;

/** One `fc_blueprints` row. Loose on purpose: a row that's wrong is refused and listed (R5), not a failed request. */
export const ImportRow = z.object({
  id: z.string().max(32),
  bp_uuid: z.string().max(100),
  name: z.string().max(200),
  created: z.string().max(40),
});
export type ImportRow = z.infer<typeof ImportRow>;

export const ImportInput = z.object({
  dryRun: z.boolean(),
  rows: z.array(ImportRow).min(1).max(IMPORT_SLICE),
});
